import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {InputHelpText} from '@/components/ui/input-help-text/InputHelpText'
import {InputStatusIcon} from '@/components/ui/input-status-icon/InputStatusIcon'
import {Label} from '@/components/ui/label/Label'
import {
    SelectComboboxInput,
    SelectComboboxList,
    SelectComboboxPopover,
    SelectItem,
    SelectTrigger,
    SelectVirtualList
} from '@/components/ui/select-atoms/SelectAtoms'
import {Skeleton} from '@/components/ui/skeleton/Skeleton'
import {formatDate} from '@/dayjs'
import {useTranslation} from '@/translations/i18n'
import {ComboboxProvider, SelectProvider} from '@ariakit/react'
import {useMemo, useRef, useState} from 'react'
import {useGetInfiniteTreatmentRequestsByPatient} from '@/features/treatment-requests/services/useGetInfiniteTreatmentRequestsByPatient'
import {useInputSearch} from '@/hooks/useInputSearch'
import {XCloseIcon} from '@/components/ui/icon/Icon'
import {TreatmentRequestSmall} from '@/features/treatment-requests/types'

interface InputSelectTreatmentRequestByPatientProps {
    patientId: string
    disabled?: boolean
    label?: string
    value?: Pick<TreatmentRequestSmall, 'id' | 'status' | 'createdAt' | 'type'> | null
    onChange: (value: TreatmentRequestSmall | null) => void
    placeholder?: string
    errorMessage?: string
    onClear?: () => void
    disableClosedRequests?: boolean
}

export const InputSelectTreatmentRequestByPatient = ({
    patientId,
    disabled,
    label,
    onChange,
    errorMessage,
    placeholder,
    onClear,
    value,
    disableClosedRequests
}: InputSelectTreatmentRequestByPatientProps) => {
    const {t} = useTranslation()
    const [isQueryEnabled, setIsQueryEnabled] = useState(false)
    const inputTreatmentRequestRef = useRef(null)
    const {searchValue, onSearch, onResetSearch} = useInputSearch()
    const {
        remappedData: treatmentRequests,
        isLoading,
        isError,
        hasNextPage,
        fetchNextPage
    } = useGetInfiniteTreatmentRequestsByPatient({
        urlParams: {patientId},
        params: {
            search: searchValue,
            limit: 10
        },
        options: {enabled: !disabled && isQueryEnabled}
    })

    const getTreatmentRequestTypeLabel = (type: string) => {
        switch (type) {
            case 'new_takeover':
                return t('treatment_requests:new_assignment')
            case 'treatment_resumption':
                return t('treatment_requests:type_treatment_resumption')
            case 'cycle_resumption':
                return t('treatment_requests:type_cycle_resumption')
            default:
                return type
        }
    }

    const treatmentRequestName = useMemo(
        () =>
            value?.createdAt && value?.type
                ? `${getTreatmentRequestTypeLabel(value.type)} - ${formatDate(value.createdAt, 'DD/MM/YYYY')}`
                : value?.id
                  ? `Richiesta ${value.id}`
                  : t('commons:select'),
        [value?.id, value?.createdAt, value?.type, t]
    )

    return (
        <ComboboxProvider setValue={onSearch} resetValueOnHide>
            <SelectProvider
                value={value?.id ?? ''}
                setValue={value => onChange(treatmentRequests.find(request => request && request.id == value) ?? null)}
                setOpen={isOpen => {
                    onResetSearch(inputTreatmentRequestRef)
                    setIsQueryEnabled(isOpen)
                }}
            >
                <Flexbox direction="column" gap={1.5} fullWidth>
                    {label && <Label>{label}</Label>}
                    <SelectTrigger
                        placeholder={placeholder}
                        fullWidth
                        value={value?.id}
                        displayValue={() => (
                            <Flexbox justify="space-between" align="center" gap={2} fullWidth>
                                {treatmentRequestName}
                                {onClear && (
                                    <XCloseIcon
                                        role="button"
                                        onClick={e => {
                                            e.preventDefault()
                                            e.stopPropagation()
                                            onClear()
                                        }}
                                    />
                                )}
                            </Flexbox>
                        )}
                        hasError={!!errorMessage}
                        disabled={disabled}
                    />

                    {errorMessage && (
                        <Flexbox align="center" gap={1.5}>
                            <InputStatusIcon hasError={!!errorMessage} />
                            <InputHelpText error={errorMessage} />
                        </Flexbox>
                    )}

                    <SelectComboboxPopover>
                        <SelectComboboxInput
                            ref={inputTreatmentRequestRef}
                            placeholder={t('commons:search_placeholder')}
                        />
                        <SelectComboboxList>
                            {isLoading ? (
                                Array.from({length: 6}, (_, index) => <Skeleton key={index} height={32} />)
                            ) : isError ? (
                                <InputHelpText error={t('errors:default')} />
                            ) : treatmentRequests.length > 0 ? (
                                <SelectVirtualList
                                    data={treatmentRequests}
                                    totalCount={treatmentRequests.length}
                                    overscan={100}
                                    endReached={() => hasNextPage && fetchNextPage()}
                                    itemContent={(itemIndex, item) => (
                                        <SelectItem
                                            disabled={disableClosedRequests && item.status === 'rejected'}
                                            hasItemCheckbox
                                            key={itemIndex}
                                            value={item.id}
                                        >
                                            {item.createdAt && item.type
                                                ? `${getTreatmentRequestTypeLabel(item.type)} - ${formatDate(item.createdAt, 'DD/MM/YYYY')}`
                                                : item.id && `Richiesta ${item.id}`}
                                        </SelectItem>
                                    )}
                                />
                            ) : (
                                <InputHelpText helpText={t('commons:no_results')} />
                            )}
                        </SelectComboboxList>
                    </SelectComboboxPopover>
                </Flexbox>
            </SelectProvider>
        </ComboboxProvider>
    )
}
